import CircularProgress from '@mui/material/CircularProgress';

import MiuThemeProvider from '@toolbox/style/theme/common/ThemeProvider/ThemeProvider';
import { Button as BaseButton } from '@toolbox/components';
import { useLeadData, useStepContext, useWidgetContext } from '@widget/core';
import { omit } from '@toolbox/utils';

import type React from 'react';
import type { ButtonProps as BaseButtonProps } from '@toolbox/components';
import type { RequireOnlyOne } from '@toolbox/utils';
import type { ReactFCWithChildren } from '@widget/types/ReactFCWithChildren';
import type { WidgetSubmitTarget } from '@widget/core/types';

type BaseProps = RequireOnlyOne<{ skip: boolean; submit: boolean }>;

type NextStepProps = {
  nextStep?: string;
  closeWidget?: boolean;
  submitWidget?: boolean;
};

export type ActionButtonProps = BaseProps &
  NextStepProps &
  Omit<BaseButtonProps, 'onClick'> & {
    submitTarget?: WidgetSubmitTarget;
  };

export const ActionButton: ReactFCWithChildren<ActionButtonProps> = (props) => {
  const step = useStepContext();
  const widget = useWidgetContext();
  const { setLoadingLead } = useLeadData();
  const { submit, skip, closeWidget, submitWidget, nextStep, submitTarget } = props;

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();
    if (submitWidget) {
      setLoadingLead(true);
    }

    if (submit) {
      const result = await step.submit(nextStep);
      if ('errors' in result) return;
    } else if (skip) {
      await step.skip(nextStep);
    } else if (closeWidget) {
      await widget.close();
    }

    if (submitWidget) {
      setTimeout(() => widget.submit(submitTarget));
    }
  };

  return (
    <BaseButton
      {...omit(props, ['submit', 'skip', 'closeWidget', 'submitWidget'])}
      type={submit || submitWidget ? 'submit' : 'button'}
      onClick={handleClick}
      disabled={step.fetching}
    >
      {step.fetching ? (
        <MiuThemeProvider>
          <CircularProgress
            size={'sm'}
            sx={{
              width: '22px',
              verticalAlign: 'middle',
            }}
          />
        </MiuThemeProvider>
      ) : (
        props.children
      )}
    </BaseButton>
  );
};
